// FILE: ComposerAttachmentChip.tsx
// Purpose: Renders filename-first composer attachments as compact pills with preview/remove actions.
// Layer: Chat composer presentation
// Depends on: composer draft attachment metadata, shared chip styles, expanded image preview helpers, and file-type icons.

import { formatAttachmentSize } from "@taskin-code/shared/attachmentKinds";
import { memo } from "react";
import { type ComposerAttachment } from "../../composerDraftStore";
import { CircleAlertIcon, XIcon } from "~/lib/icons";
import { cn } from "~/lib/utils";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { COMPOSER_INLINE_CHIP_DISMISS_BUTTON_CLASS_NAME } from "../composerInlineChip";
import { buildExpandedImagePreview, type ExpandedImagePreview } from "./ExpandedImagePreview";
import { VscodeEntryIcon } from "./VscodeEntryIcon";

interface ComposerAttachmentChipProps {
  attachment: ComposerAttachment;
  attachments: readonly ComposerAttachment[];
  nonPersisted: boolean;
  resolvedTheme: "light" | "dark";
  onExpandImage: (preview: ExpandedImagePreview) => void;
  onRemoveAttachment: (attachmentId: string) => void;
}

export const ComposerAttachmentChip = memo(function ComposerAttachmentChip({
  attachment,
  attachments,
  nonPersisted,
  resolvedTheme,
  onExpandImage,
  onRemoveAttachment,
}: ComposerAttachmentChipProps) {
  const isImage = attachment.type === "image";

  return (
    <div className="inline-flex min-w-0 max-w-full items-center gap-0.5 rounded-full border border-border/60 bg-background/92 p-0.5 shadow-[0_1px_0_rgba(255,255,255,0.14)_inset]">
      {/*
        A file has nothing to expand — the lightbox only knows how to show
        images — so it renders as a plain, non-interactive label rather than a
        button that would look clickable and then do nothing.
      */}
      {isImage ? (
        <button
          type="button"
          className="flex min-w-0 max-w-[232px] items-center gap-1.5 rounded-full py-0 pl-0 pr-0.5 text-left transition-colors hover:bg-accent/28 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label={`Preview ${attachment.name}`}
          onClick={() => {
            const preview = buildExpandedImagePreview(attachments, attachment.id);
            if (!preview) return;
            onExpandImage(preview);
          }}
        >
          <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border/55 bg-muted/35">
            {attachment.previewUrl ? (
              <img
                src={attachment.previewUrl}
                alt={attachment.name}
                className="size-full object-cover"
              />
            ) : (
              <span className="px-1 text-[9px] font-medium uppercase tracking-[0.08em] text-muted-foreground/70">
                IMG
              </span>
            )}
          </span>
          <span className="min-w-0 truncate text-[12px] font-medium text-foreground/84">
            {attachment.name}
          </span>
        </button>
      ) : (
        <span
          className="flex min-w-0 max-w-[232px] items-center gap-1.5 rounded-full py-0 pl-0 pr-0.5 text-left"
          title={`${attachment.name} · ${formatAttachmentSize(attachment.sizeBytes)}`}
        >
          <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border/55 bg-muted/35">
            <VscodeEntryIcon
              pathValue={attachment.name}
              kind="file"
              theme={resolvedTheme}
              className="size-3.5"
            />
          </span>
          <span className="min-w-0 truncate text-[12px] font-medium text-foreground/84">
            {attachment.name}
          </span>
          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/70">
            {formatAttachmentSize(attachment.sizeBytes)}
          </span>
        </span>
      )}

      {nonPersisted && (
        <Tooltip>
          <TooltipTrigger
            render={
              <span
                role="img"
                aria-label="Draft attachment may not persist"
                className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-amber-600"
              >
                <CircleAlertIcon className="size-3" />
              </span>
            }
          />
          <TooltipPopup side="top" className="max-w-64 whitespace-normal leading-tight">
            Draft attachment could not be saved locally and may be lost on navigation.
          </TooltipPopup>
        </Tooltip>
      )}

      <button
        type="button"
        className={cn(
          COMPOSER_INLINE_CHIP_DISMISS_BUTTON_CLASS_NAME,
          "size-5 rounded-full text-muted-foreground/62 hover:bg-accent/36 hover:text-foreground",
        )}
        onClick={() => onRemoveAttachment(attachment.id)}
        aria-label={`Remove ${attachment.name}`}
      >
        <XIcon className="size-3" />
      </button>
    </div>
  );
});
